<script lang="ts">
    import { createEventDispatcher } from 'svelte'
    import Fa from 'svelte-fa'
    import { faCheck, faRotate, faUpload, faXmark } from '@fortawesome/free-solid-svg-icons'
    import Zinput from '../../_common/z/zinput.svelte'
    import ZContainer from '../../_common/z/z-container.svelte'
    import type { DocumentHeaderFooter } from '../types/document-header-footer'
    import { LOGO_MAX_HEIGHT, LOGO_MAX_WIDTH } from '../utils/image-limits'

    export let headerFooter: DocumentHeaderFooter
    export let requiredFields: string[] = []

    // Templates salvos antes desse campo existir não têm "signature" no headerFooter — preenche
    // com o padrão desligado pra não quebrar o bind abaixo.
    if (!headerFooter.signature) {
        headerFooter.signature = { requested: false, mode: 1 }
    }

    const MODE_LINK = 1
    const MODE_EMAIL = 2
    const modeOptions = [
        { label: 'Link de assinatura (assinatura simples)', value: MODE_LINK },
        { label: 'Assinatura por e-mail (assinatura avançada)', value: MODE_EMAIL },
    ]

    // Mesmas opções/valores da versão anterior dos formulários (customer-service-models.svelte) —
    // a validação (form-v2-form-editor.svelte) checa esses campos direto no cadastro da pessoa
    // vinculada, independente de quais blocos existem no template.
    const requiredFieldsOptions = [
        { value: 'email', label: 'e-mail' },
        { value: 'address', label: 'endereço' },
        { value: 'document', label: 'documento' },
    ]

    const dispatch = createEventDispatcher()

    let logoInputEl: HTMLInputElement

    function onLogoSelected(event: Event) {
        const input = event.currentTarget as HTMLInputElement
        const file = input.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = async () => {
            // Reduz já aqui: o logo é repintado a cada página na impressão, então é o pior lugar
            // possível para uma imagem em resolução original. Ver utils/image-limits.ts.
            headerFooter.header.logo = await window.api.utils.images.resizeBase64(reader.result as string, LOGO_MAX_WIDTH, LOGO_MAX_HEIGHT)
        }
        reader.readAsDataURL(file)
        input.value = ''
    }

    function removeLogo() {
        headerFooter.header.logo = null
    }
</script>

<div class="settings-backdrop">
    <div class="settings-modal">
        <div class="settings-header">
            <div>
                <h2>Propriedades do documento</h2>
                <p>Cabeçalho e rodapé não são um bloco arrastável — ficam fixos no topo e no rodapé do documento, tanto no design quanto no Visualizar. Ideal para montar o timbre de uma receita médica, por exemplo.</p>
            </div>
            <button type="button" class="close-btn" on:click={() => dispatch('close')}>
                <Fa icon={faXmark} />
            </button>
        </div>

        <div class="settings-body">
            <section class="settings-section">
                <label class="section-toggle">
                    <input type="checkbox" bind:checked={headerFooter.header.enabled} />
                    <span class="switch" />
                    <span class="section-title">Exibir cabeçalho</span>
                </label>

                {#if headerFooter.header.enabled}
                    <div class="section-fields">
                        <label class="section-toggle sub-toggle">
                            <input type="checkbox" bind:checked={headerFooter.header.useCompanyData} />
                            <span class="switch" />
                            <span class="section-title">Usar os dados da empresa</span>
                        </label>

                        {#if headerFooter.header.useCompanyData}
                            <p class="company-data-note">
                                Logo, título, subtítulo, endereço e contato serão preenchidos automaticamente com os dados cadastrados da empresa.
                            </p>
                        {:else}
                            <div class="logo-field">
                                <span class="field-label">Logo (opcional)</span>
                                {#if headerFooter.header.logo}
                                    <div class="logo-preview">
                                        <img src={headerFooter.header.logo} alt="Logo" />
                                        <div class="logo-actions">
                                            <button type="button" on:click={() => logoInputEl.click()} title="Trocar logo">
                                                <Fa icon={faRotate} />
                                            </button>
                                            <button type="button" on:click={removeLogo} title="Remover logo">
                                                <Fa icon={faXmark} />
                                            </button>
                                        </div>
                                    </div>
                                {:else}
                                    <button type="button" class="logo-empty" on:click={() => logoInputEl.click()}>
                                        <Fa icon={faUpload} />
                                        <span>Selecionar imagem</span>
                                    </button>
                                {/if}
                                <input bind:this={logoInputEl} type="file" accept="image/*" hidden on:change={onLogoSelected} />
                            </div>

                            <ZContainer gapRow="8px" gapCol="8px">
                                <Zinput
                                    label="Título (nome do profissional ou da clínica)"
                                    size={12}
                                    sizeM={12}
                                    type="text"
                                    bind:value={headerFooter.header.title}
                                />
                                <Zinput
                                    label="Subtítulo (especialidade / registro profissional)"
                                    size={12}
                                    sizeM={12}
                                    type="text"
                                    bind:value={headerFooter.header.subtitle}
                                />
                                <Zinput
                                    label="Endereço"
                                    size={12}
                                    sizeM={12}
                                    type="text"
                                    bind:value={headerFooter.header.addressLine}
                                />
                                <Zinput
                                    label="Contato (telefone / e-mail)"
                                    size={12}
                                    sizeM={12}
                                    type="text"
                                    bind:value={headerFooter.header.contactLine}
                                />
                            </ZContainer>
                        {/if}
                    </div>
                {/if}
            </section>

            <section class="settings-section">
                <label class="section-toggle">
                    <input type="checkbox" bind:checked={headerFooter.footer.enabled} />
                    <span class="switch" />
                    <span class="section-title">Exibir rodapé</span>
                </label>

                {#if headerFooter.footer.enabled}
                    <div class="section-fields">
                        <ZContainer gapRow="8px" gapCol="8px">
                            <Zinput
                                label="Texto do rodapé (endereço, telefone, observações legais...)"
                                size={12}
                                sizeM={12}
                                type="textarea"
                                rows={3}
                                bind:value={headerFooter.footer.text}
                            />
                        </ZContainer>
                    </div>
                {/if}
            </section>

            <section class="settings-section">
                <label class="section-toggle">
                    <input type="checkbox" bind:checked={headerFooter.signature.requested} />
                    <span class="switch" />
                    <span class="section-title">Solicitar assinatura ao preencher</span>
                </label>

                {#if headerFooter.signature.requested}
                    <div class="section-fields">
                        <ZContainer gapRow="8px" gapCol="8px">
                            <Zinput
                                label="Modalidade de assinatura"
                                size={12}
                                sizeM={12}
                                type="select"
                                options={modeOptions}
                                bind:value={headerFooter.signature.mode}
                            />
                        </ZContainer>
                    </div>
                {/if}
            </section>

            <section class="settings-section">
                <span class="section-title">Campos obrigatórios da pessoa</span>
                <div class="section-fields no-indent">
                    <ZContainer gapRow="8px" gapCol="8px">
                        <Zinput
                            label="Exigir no cadastro do cliente ao preencher"
                            size={12}
                            sizeM={12}
                            type="multiselect"
                            options={requiredFieldsOptions}
                            bind:value={requiredFields}
                        />
                    </ZContainer>
                </div>
            </section>
        </div>

        <div class="settings-footer">
            <button type="button" class="done-btn" on:click={() => dispatch('close')}>
                <Fa icon={faCheck} />
                <span>Concluir</span>
            </button>
        </div>
    </div>
</div>

<style>
    .settings-backdrop {
        position: fixed;
        inset: 0;
        z-index: 1200;
        background: rgba(17, 19, 24, 0.65);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
    }
    .settings-modal {
        background: #fff;
        border-radius: 14px;
        width: 100%;
        max-width: 560px;
        max-height: 88vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }
    .settings-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 14px;
        padding: 18px 20px;
        border-bottom: 1px solid #ecedf0;
    }
    .settings-header h2 {
        margin: 0 0 4px;
        font-size: 1rem;
        font-weight: 600;
        color: #212529;
    }
    .settings-header p {
        margin: 0;
        font-size: 0.78rem;
        color: #868e96;
        max-width: 440px;
    }
    .close-btn {
        flex-shrink: 0;
        border: none;
        background: #f1f3f5;
        color: #495057;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        cursor: pointer;
    }
    .close-btn:hover {
        background: #e9ecef;
    }
    .settings-body {
        overflow-y: auto;
        padding: 18px 20px;
        display: flex;
        flex-direction: column;
        gap: 22px;
    }
    .settings-section {
        display: flex;
        flex-direction: column;
        gap: 14px;
    }
    .section-toggle {
        display: flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;
    }
    .section-toggle input {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
    }
    .switch {
        position: relative;
        width: 32px;
        height: 18px;
        flex-shrink: 0;
        border-radius: 999px;
        background: #ced4da;
        transition: background 0.15s ease;
    }
    .switch::after {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: #fff;
        transition: transform 0.15s ease;
    }
    .section-toggle input:checked + .switch {
        background: var(--cs2-accent, #4263eb);
    }
    .section-toggle input:checked + .switch::after {
        transform: translateX(14px);
    }
    .section-title {
        font-size: 0.92rem;
        font-weight: 600;
        color: #212529;
    }
    .section-fields {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding-left: 42px;
    }
    .section-fields.no-indent {
        padding-left: 0;
    }
    .sub-toggle {
        margin-bottom: 4px;
    }
    .sub-toggle .switch {
        width: 28px;
        height: 16px;
    }
    .sub-toggle .switch::after {
        width: 12px;
        height: 12px;
    }
    .sub-toggle input:checked + .switch::after {
        transform: translateX(12px);
    }
    .sub-toggle .section-title {
        font-size: 0.82rem;
        font-weight: 500;
    }
    .company-data-note {
        margin: 0;
        font-size: 0.78rem;
        color: #868e96;
        max-width: 400px;
    }
    .field-label {
        font-size: 0.76rem;
        font-weight: 500;
        color: #868e96;
        margin-bottom: 4px;
    }
    .logo-field {
        margin-bottom: 8px;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .logo-empty {
        width: 110px;
        height: 80px;
        border: 1px dashed #ced4da;
        border-radius: 10px;
        background: #f8f9fa;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 6px;
        color: #868e96;
        font-size: 0.72rem;
        cursor: pointer;
    }
    .logo-empty:hover {
        border-color: var(--cs2-accent, #4263eb);
        color: var(--cs2-accent, #4263eb);
    }
    .logo-preview {
        position: relative;
        width: 110px;
        height: 80px;
        border-radius: 10px;
        overflow: hidden;
        background: #f1f3f5;
    }
    .logo-preview img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
    }
    .logo-actions {
        position: absolute;
        inset: 0;
        background: rgba(17, 19, 24, 0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        opacity: 0;
        transition: opacity 0.15s ease;
    }
    .logo-preview:hover .logo-actions {
        opacity: 1;
    }
    .logo-actions button {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        border: none;
        background: #fff;
        color: #495057;
        cursor: pointer;
    }
    .settings-footer {
        display: flex;
        justify-content: flex-end;
        padding: 14px 20px;
        border-top: 1px solid #ecedf0;
    }
    .done-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        border: none;
        border-radius: 8px;
        padding: 9px 18px;
        font-size: 0.85rem;
        font-weight: 500;
        background: var(--cs2-accent, #4263eb);
        color: #fff;
        cursor: pointer;
    }
    .done-btn:hover {
        background: #364fc7;
    }

    /* ───── tema escuro ─────
       O modal usa a paleta escura do projeto enquanto as entradas (Zinput)
       recebem automaticamente o tema escuro via css_global.css/.zinput.
    */
    :global(.theme_dark) .settings-modal {
        background: #1e1e1e;
        border: 1px solid #2d2d2d;
    }
    :global(.theme_dark) .settings-header {
        border-bottom-color: #2d2d2d;
    }
    :global(.theme_dark) .settings-header h2 {
        color: rgb(var(--foreground-color));
    }
    :global(.theme_dark) .settings-header p {
        color: #555;
    }
    :global(.theme_dark) .close-btn {
        background: #2a2a2a;
        color: #aaa;
    }
    :global(.theme_dark) .close-btn:hover {
        background: #333;
    }
    :global(.theme_dark) .section-title {
        color: rgb(var(--foreground-color));
    }
    :global(.theme_dark) .field-label {
        color: #555;
    }
    :global(.theme_dark) .company-data-note {
        color: #555;
    }
    :global(.theme_dark) .logo-empty {
        background: #252525;
        border-color: #3a3a3a;
        color: #666;
    }
    :global(.theme_dark) .logo-empty:hover {
        border-color: var(--cs2-accent, #4263eb);
        color: var(--cs2-accent, #4263eb);
    }
    :global(.theme_dark) .logo-preview {
        background: #2a2a2a;
    }
    :global(.theme_dark) .settings-footer {
        border-top-color: #2d2d2d;
    }
</style>
